'use client';

import type { AutocompleteProps } from '@magicschool/spellbook/components/Autocomplete';
import type { AutocompleteBaseContextState, AutocompleteOptionType } from '@magicschool/spellbook/lib/autocomplete';
import {
  getOptionByValue as getOptionByValueUtil,
  isPrimaryButtonClick,
  SEARCH_OVERRIDE_VALUE,
  useAutocomplete as useAutocompleteUtil,
} from '@magicschool/spellbook/lib/autocomplete';
import { getLabelFromValue } from '@magicschool/spellbook/lib/select';
import type { ChangeEvent, FC, KeyboardEvent, MouseEvent, RefObject } from 'react';
import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';

export type AutocompleteContextType = AutocompleteBaseContextState<AutocompleteOptionType> &
  Pick<
    AutocompleteProps,
    | 'disableDefaultFiltering'
    | 'disableDefaultSearchOption'
    | 'defaultValue'
    | 'descriptionOrientation'
    | 'disableTextHighlighting'
    | 'isLoading'
    | 'text'
    | 'onBlur'
    | 'onCloseAutoFocus'
    | 'onEscapeKeyDown'
    | 'onFocus'
    | 'onInputChange'
    | 'onInputClear'
    | 'onInputSubmit'
    | 'onOpenChange'
    | 'onPointerDownOutside'
    | 'onValueChange'
    | 'disableDefaultNoResults'
    | 'type'
    | 'value'
  > & {
    currentValue: string;
    label: string;
    setLabel: (label: string) => void;
    setOpen: (open: boolean) => void;
    highlightedIndex: number;
    setHighlightedOption: (index: number) => void;
    getHighlightedOption: () => AutocompleteOptionType | null;
    clearHighlightedOption: () => void;
  };

export type AutocompleteProviderProps = Pick<
  AutocompleteProps,
  | 'allowFreeText'
  | 'children'
  | 'disableDefaultFiltering'
  | 'disableDefaultSearchOption'
  | 'defaultValue'
  | 'descriptionOrientation'
  | 'disabled'
  | 'disableTextHighlighting'
  | 'inputValue'
  | 'isLoading'
  | 'text'
  | 'onBlur'
  | 'onCloseAutoFocus'
  | 'onEscapeKeyDown'
  | 'onFocus'
  | 'onInputChange'
  | 'onInputClear'
  | 'onInputSubmit'
  | 'onOpenChange'
  | 'onPointerDownOutside'
  | 'onValueChange'
  | 'readOnly'
  | 'disableDefaultNoResults'
  | 'type'
  | 'value'
> & {
  initialOptions?: Map<string, { label: string; value: string }>;
  inputRef: RefObject<HTMLInputElement | null>;
  open?: boolean;
};

const AutocompleteContext = createContext<AutocompleteContextType>({
  clearSearchStringIfNoValueMatches: () => undefined,
  currentValue: '',
  disableDefaultFiltering: undefined,
  disableDefaultNoResults: undefined,
  descriptionOrientation: 'horizontal',
  getOptionByValue: () => undefined,
  handleInputOnBlur: () => undefined,
  handleInputOnChange: () => undefined,
  handleInputOnClick: () => undefined,
  handleInputOnFocus: () => undefined,
  handleInputOnKeyDown: () => undefined,
  handleInputOnClear: () => undefined,
  handleInputSubmit: () => undefined,
  handleOpenChange: () => undefined,
  handleOptionOnClick: () => undefined,
  handleOptionOnKeyDown: () => undefined,
  handleOptionOnMouseEnter: () => undefined,
  handleTriggerOnBlur: () => undefined,
  handleTriggerOnClick: () => undefined,
  handleTriggerOnFocus: () => undefined,
  inputRef: { current: null },
  isControlled: false,
  isLoading: false,
  label: '',
  open: false,
  optionRefs: [],
  options: new Map(),
  registerOption: () => undefined,
  searchString: '',
  setLabel: () => undefined,
  setOpen: () => undefined,
  setSearchString: () => undefined,
  type: 'select',
  unregisterOption: () => undefined,
  highlightedIndex: 0,
  setHighlightedOption: () => undefined,
  getHighlightedOption: () => null,
  clearHighlightedOption: () => undefined,
});

export const AutocompleteProvider: FC<AutocompleteProviderProps> = ({
  allowFreeText,
  children,
  disableDefaultFiltering,
  disableDefaultSearchOption,
  defaultValue,
  descriptionOrientation,
  disabled,
  disableTextHighlighting,
  initialOptions,
  inputRef,
  inputValue,
  isLoading,
  text,
  onBlur,
  onCloseAutoFocus,
  onEscapeKeyDown,
  onFocus,
  onInputChange,
  onInputClear,
  onInputSubmit,
  onOpenChange,
  onPointerDownOutside,
  onValueChange,
  readOnly,
  disableDefaultNoResults,
  type,
  value,
  open: providedOpen,
}) => {
  const optionLabelCacheRef = useRef<Record<string, string>>({});

  const initialOptionsMap = useMemo(() => {
    if (initialOptions?.size) {
      const initialMap = new Map<string, AutocompleteOptionType>();
      initialOptions.forEach((option, key) => {
        initialMap.set(key, {
          value: option.value,
          label: option.label,
          disabled: false,
          highlightedLabel: option.label,
        });

        optionLabelCacheRef.current[key] = option.label;
      });
      return initialMap;
    }
    return new Map<string, AutocompleteOptionType>();
  }, [initialOptions]);

  const [internalValue, setInternalValue] = useState<string>(defaultValue || '');
  const [label, setLabel] = useState<string>('');

  const isControlled = value !== undefined;
  const currentValue = isControlled ? value! : internalValue;
  const isControlledOpen = providedOpen !== undefined;
  const currentOpen = isControlledOpen ? providedOpen : false;

  const isEnabled = !disabled && !readOnly;

  const {
    clearSearchStringIfNoValueMatches,
    handleInputOnBlur,
    handleInputOnChange: handleInputOnChangeUtil,
    handleInputOnClick: handleInputOnClickUtil,
    handleInputOnClear: handleInputOnClearUtil,
    handleInputOnKeyDown: handleInputOnKeyDownUtil,
    handleOpenChange,
    handleOptionOnClick: handleOptionOnClickUtil,
    handleOptionOnKeyDownArrowDown,
    handleOptionOnKeyDownArrowUp,
    handleOptionOnKeyDownEnter,
    handleOptionOnMouseEnter,
    handleOptionOnKeyDownEscape,
    handleTriggerOnBlur,
    handleTriggerOnClick,
    handleTriggerOnFocus,
    open,
    setOpen,
    optionLabelCache,
    optionRefs,
    options,
    registerOption,
    searchString,
    setOptions,
    setSearchString,
    unregisterOption,
    highlightedIndex,
    setHighlightedOption,
    getHighlightedOption,
    clearHighlightedOption,
  } = useAutocompleteUtil<AutocompleteOptionType>({
    allowFreeText,
    isEnabled,
    inputRef,
    inputValue,
    onBlur,
    onFocus,
    open: isControlledOpen ? currentOpen : undefined,
    onInputChange,
    onInputSubmit,
    onOpenChange,
  });

  const handleInputOnFocusWithSelectedOption = useCallback(
    (event: React.FocusEvent<HTMLInputElement>) => {
      if (onFocus) {
        const buttonEvent = {
          ...event,
          currentTarget: event.currentTarget as unknown as HTMLButtonElement,
          target: event.target as unknown as HTMLButtonElement,
        } as React.FocusEvent<HTMLButtonElement>;
        onFocus(buttonEvent);
      }
    },
    [onFocus],
  );

  const handleOpenChangeWithHighlighting = useCallback(
    (newOpen: boolean) => {
      handleOpenChange(newOpen);
    },
    [handleOpenChange],
  );

  const getOptionByValue = useCallback(
    (value: string) => {
      return getOptionByValueUtil(value, options, optionLabelCache);
    },
    [options, optionLabelCache],
  );

  const handleInputSubmit = useCallback(
    (value?: string) => {
      if (isEnabled) {
        const newValue = value || currentValue || searchString;
        if (newValue.length) {
          onInputSubmit?.(newValue);
        }
        setOpen(false);
        onOpenChange?.(false);
      }
    },
    [isEnabled, onInputSubmit, currentValue, searchString, setOpen, onOpenChange],
  );

  const handleValueChange = useCallback(
    (newValue: string) => {
      if (isEnabled) {
        if (!isControlled) {
          setInternalValue(newValue);
        }

        const option = getOptionByValue(newValue);
        if (option) {
          setLabel(option.label);
          setSearchString(option.label);
        }

        onValueChange?.(newValue);

        if (inputRef.current) {
          inputRef.current.focus();
        }

        if (!newValue && !isControlledOpen) {
          setOpen(true);
          onOpenChange?.(true);
        }
      }
    },
    [
      isEnabled,
      isControlled,
      getOptionByValue,
      onValueChange,
      setOpen,
      onOpenChange,
      isControlledOpen,
      setLabel,
      setSearchString,
      inputRef,
    ],
  );

  const handleInputOnChange = useCallback(
    (event: ChangeEvent<HTMLInputElement>) => {
      if (isEnabled) {
        handleInputOnChangeUtil(event);

        if (!isControlled) {
          setInternalValue('');
        } else {
          onValueChange?.('');
        }

        setLabel('');
      }
    },
    [isEnabled, handleInputOnChangeUtil, isControlled, setLabel, onValueChange, setHighlightedOption, options],
  );

  const handleInputOnClear = useCallback(() => {
    if (isEnabled) {
      handleInputOnClearUtil();
      setLabel('');

      if (!isControlled) {
        setInternalValue('');
      } else {
        onValueChange?.('');
      }

      if (inputRef.current) {
        handleOpenChange(true);
        inputRef.current?.focus();
      }

      onInputClear?.();
    }
  }, [isEnabled, handleInputOnClearUtil, isControlled, setLabel, onValueChange, onInputClear, inputRef, handleOpenChange]);

  const handleInputOnClick = useCallback(
    (event: MouseEvent<HTMLInputElement>) => {
      handleInputOnClickUtil(event);

      if (isEnabled && !isControlledOpen && isPrimaryButtonClick(event)) {
        handleOpenChange(true);
      }
    },
    [handleInputOnClickUtil, isEnabled, handleOpenChange, isControlledOpen],
  );

  const handleInputOnKeyDownWithValueChange = useCallback(
    (event: KeyboardEvent<HTMLInputElement>) => {
      if (isEnabled && event.key === 'Enter') {
        const highlightedOption = getHighlightedOption?.();
        if (highlightedOption && (!searchString || searchString.trim() === '')) {
          event.preventDefault();

          handleValueChange(highlightedOption.value);
          onInputSubmit?.(highlightedOption.value);
          if (!isControlledOpen) {
            handleOpenChange(false);
          }
          return;
        }
      }

      handleInputOnKeyDownUtil(event);
    },
    [
      isEnabled,
      getHighlightedOption,
      searchString,
      handleValueChange,
      onInputSubmit,
      isControlledOpen,
      handleOpenChange,
      handleInputOnKeyDownUtil,
    ],
  );

  const handleOptionOnClick = useCallback(
    (event: MouseEvent<HTMLDivElement>, value: string) => {
      if (isEnabled) {
        const entry = options.get(value);

        if (entry && !entry.disabled) {
          handleOptionOnClickUtil(event, value);
          handleValueChange(entry.value!);
          handleInputSubmit(entry.value!);
        }
      }
    },
    [isEnabled, options, handleValueChange],
  );

  const handleOptionOnKeyDown = useCallback(
    (event: KeyboardEvent<HTMLDivElement>, index: number) => {
      if (isEnabled) {
        const optionValues = Array.from(options.values());
        const entry = optionValues[index] && optionValues[index].value !== SEARCH_OVERRIDE_VALUE ? optionValues[index] : undefined;
        const value = entry?.value || currentValue || searchString;

        if (entry?.disabled) {
          return;
        }

        switch (event.key) {
          case 'Enter':
          case ' ': {
            handleOptionOnKeyDownEnter(event);
            handleValueChange(value);
            handleInputSubmit(value!);

            break;
          }
          case 'Tab': {
            event.preventDefault();
            handleOpenChange(false);
            clearHighlightedOption?.();
            if (inputRef.current) {
              inputRef.current.focus();
            }
            break;
          }
          case 'ArrowDown': {
            handleOptionOnKeyDownArrowDown(event, optionRefs, index);

            break;
          }
          case 'ArrowUp': {
            handleOptionOnKeyDownArrowUp(event, optionRefs, index, setHighlightedOption);
            break;
          }
          case 'Escape':
          case 'Esc': {
            handleOptionOnKeyDownEscape(event);
            break;
          }
        }
      }
    },
    [
      isEnabled,
      options,
      handleOptionOnKeyDownEnter,
      handleValueChange,
      handleOptionOnKeyDownArrowDown,
      handleOptionOnKeyDownArrowUp,
      handleOptionOnKeyDownEscape,
      optionRefs,
      onInputSubmit,
      allowFreeText,
    ],
  );

  useEffect(() => {
    const newCache: Record<string, string> = { ...optionLabelCache.current };

    Array.from(options.values()).forEach((option) => {
      newCache[option.value] = option.label;
    });

    optionLabelCache.current = newCache;
  }, [options, optionLabelCache]);

  useEffect(() => {
    if (currentValue) {
      const cachedLabel = optionLabelCache.current[currentValue];

      if (cachedLabel) {
        setLabel(cachedLabel);
      } else {
        const labelText = getLabelFromValue(currentValue, Array.from(options.values()));

        if (labelText) {
          setLabel(labelText);

          optionLabelCache.current[currentValue] = labelText;
        }
      }
    } else {
      setLabel('');
    }
  }, [currentValue, options, getLabelFromValue, isControlled]);

  // Rebuilds from initialOptionsMap (instead of only merging into the prior map) so an option no
  // longer among the current children — e.g. a stale result from a prior search — gets dropped
  // rather than lingering and desyncing arrow-key/Enter selection from what's actually rendered.
  // Existing entries win over initialOptionsMap's simplified copy so already-registered
  // `disabled`/highlight data isn't downgraded. Guarded on initialOptionsMap.size: it only ever
  // walks direct <Autocomplete.Option> children, so it's permanently empty for consumers whose
  // options live inside Autocomplete.Content (registered via registerOption instead, e.g. both
  // ToolSearchBars) -- without the guard this would rebuild an empty map and wipe those out on
  // every keystroke-driven re-render.
  useEffect(() => {
    if (!initialOptionsMap.size) {
      return;
    }

    setOptions((prevOptions) => {
      const searchOption = prevOptions.get(SEARCH_OVERRIDE_VALUE);
      const newOptions = new Map<string, AutocompleteOptionType>();

      initialOptionsMap.forEach((option, key) => {
        newOptions.set(key, prevOptions.get(key) ?? option);
      });

      if (searchOption) {
        newOptions.set(SEARCH_OVERRIDE_VALUE, searchOption);
      }

      return newOptions;
    });
  }, [initialOptionsMap, setOptions]);

  const contextProps = {
    disableDefaultFiltering,
    disableDefaultSearchOption,
    defaultValue,
    descriptionOrientation,
    disableTextHighlighting,
    isLoading,
    text,
    onCloseAutoFocus,
    onEscapeKeyDown,
    onInputChange,
    onInputClear,
    onOpenChange,
    onPointerDownOutside,
    onValueChange,
    type,
    value,
  };

  const contextState = {
    currentValue,
    isControlled,
    label,
    open,
    options,
    searchString,
    disableDefaultNoResults,
  };

  const contextHandlers = {
    clearSearchStringIfNoValueMatches,
    getOptionByValue,
    handleInputOnBlur,
    handleInputOnChange,
    handleInputSubmit,
    handleInputOnClick,
    handleInputOnFocus: handleInputOnFocusWithSelectedOption,
    handleInputOnClear,
    handleInputOnKeyDown: handleInputOnKeyDownWithValueChange,
    handleOpenChange: handleOpenChangeWithHighlighting,
    handleOptionOnClick,
    handleOptionOnKeyDown,
    handleOptionOnMouseEnter,
    handleTriggerOnBlur,
    handleTriggerOnClick,
    handleTriggerOnFocus,
    registerOption,
    setLabel,
    setOpen,
    setSearchString,
    unregisterOption,
  };

  const contextValue = useMemo<AutocompleteContextType>(
    () => ({
      ...contextProps,
      ...contextState,
      ...contextHandlers,
      inputRef,
      optionRefs,
      highlightedIndex,
      setHighlightedOption,
      getHighlightedOption,
      clearHighlightedOption,
    }),
    [
      contextProps,
      contextState,
      contextHandlers,
      inputRef,
      optionRefs,
      highlightedIndex,
      setHighlightedOption,
      getHighlightedOption,
      clearHighlightedOption,
    ],
  );

  return <AutocompleteContext.Provider value={contextValue}>{children}</AutocompleteContext.Provider>;
};

export const useAutocomplete = () => useContext(AutocompleteContext);

export default AutocompleteProvider;
